/**
 * 321 (FED-74). Searchable picking, one way for every picker.
 *
 * David, 29 Oct: the site "should be a searchable list rather than full drop
 * down (actually all of them should be searchable)". So equipment, sector,
 * feature, brand and site are all chosen by typing into one control
 * (components/SearchPick), and this is the one fold and the one ranking it
 * uses: accents and case do not matter ("Satelite" finds "Satélite"), a name
 * that starts with what was typed comes first, then a word that does, then
 * anything containing it, then every typed word somewhere.
 */
export type PickOption = { id: string; label: string; hint?: string | null }

/** Lower case, no accents, words only — the same fold the server uses for site names (app.photo_site_key). */
export function foldText(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** The options that match what was typed, best first; everything (in its own order) when nothing was typed. */
export function matchOptions<T extends PickOption>(options: T[], query: string, limit = 50): T[] {
  const q = foldText(query)
  if (!q) return options.slice(0, limit)
  const words = q.split(' ')
  const scored: { o: T; s: number; i: number }[] = []
  options.forEach((o, i) => {
    const f = foldText(o.label)
    let s = -1
    if (f === q) s = 0
    else if (f.startsWith(q)) s = 1
    else if ((' ' + f).includes(' ' + q)) s = 2
    else if (f.includes(q)) s = 3
    else if (words.every((w) => f.includes(w))) s = 4
    if (s >= 0) scored.push({ o, s, i })
  })
  scored.sort((a, b) => a.s - b.s || a.i - b.i)
  return scored.slice(0, limit).map((x) => x.o)
}

/** True when the typed text already IS one of the options, so "Add …" is not offered for it. */
export function hasExact(options: PickOption[], query: string): boolean {
  const q = foldText(query)
  return !!q && options.some((o) => foldText(o.label) === q)
}
